import { useEffect, useMemo, useState } from "react";

import { useNotification, useAuth } from "../../../hooks";
import { useCart, useSupplier } from "../../../hooks/query";
import type { LineEdit } from "../types";
import type { MaterialRowItem } from "../utils/materialLineItems";
import { buildSupplierComparisonValues } from "../utils/buildSupplierComparisonValues";
import { cloneMaterialEdit, hasMaterialChanges, normalizeUnitPrice } from "../utils/cartMapping";
import useEstimateContractContextManager from "./useEstimateContractContextManager";

type UseEstimateSupplierComparisonOptions = {
    projectId?: string;
    materialsByGroup: Record<string, MaterialRowItem[]>;
};

function buildItemSupplierSelections(
    selectedValues: Record<string, { manufacturerProductId?: string; value?: string; supplierName?: string }>,
) {
    const mapping: Record<string, string> = {};

    for (const item of Object.values(selectedValues)) {
        if (!item?.supplierName) continue;
        const key = item.manufacturerProductId || item.value;
        if (key) {
            mapping[key] = item.supplierName;
        }
    }

    return mapping;
}

function resolveComparisonUnitPrice(
    comparisonResults: any[],
    supplierName: string,
    item: { manufacturerProductId?: string; value?: string },
) {
    const itemKey = item.manufacturerProductId || item.value;
    if (!itemKey) return null;

    const comparisonSupplierData = comparisonResults.find(
        (supplier) => supplier.SupplierName === supplierName,
    );
    if (!comparisonSupplierData?.SupplierInventory) {
        return null;
    }

    const itemData = comparisonSupplierData.SupplierInventory.find(
        (inventory: any) => {
            const inventoryKey =
                inventory.ManufacturerProductId || inventory.ProductName;
            return inventoryKey === itemKey;
        },
    );

    if (itemData?.UnitPrice === null || itemData?.UnitPrice === undefined) {
        return null;
    }

    const price = Number(itemData.UnitPrice);
    return Number.isFinite(price) && price > 0 ? price : null;
}

export function useEstimateSupplierComparison({
    projectId,
    materialsByGroup,
}: UseEstimateSupplierComparisonOptions) {
    const { values, referenceValue, patchMaterialEdits, setReferenceValue } =
        useEstimateContractContextManager();
    const { showSuccess, showError } = useNotification();
    const { selectedOrganization } = useAuth();
    const { useSupplierComparison, useList } = useSupplier();
    const comparisonSvc = useSupplierComparison();
    const { mutateAsync: updateCartItem } = useCart().useUpdate();
    const { data: suppliers = [], isLoading: isLoadingSuppliers } = useList({
        OrganizationId: selectedOrganization?.Id,
        Type: "Manufacturer",
    });

    const selectedValues = useMemo(
        () =>
            buildSupplierComparisonValues(
                materialsByGroup,
                values.materials,
                referenceValue.cartIds || {},
            ),
        [materialsByGroup, values.materials, referenceValue.cartIds],
    );

    const comparableItemCount = Object.keys(selectedValues).length;
    const hasUnsavedMaterialChanges = hasMaterialChanges(
        values.materials,
        referenceValue.materials,
    );
    const canCompare =
        !!projectId &&
        comparableItemCount > 0 &&
        !hasUnsavedMaterialChanges;

    const itemSupplierSelections = useMemo(
        () => buildItemSupplierSelections(selectedValues),
        [selectedValues],
    );

    const [hasInitializedSuppliers, setHasInitializedSuppliers] =
        useState(false);
    const [selectedSupplierNames, setSelectedSupplierNames] = useState<
        string[]
    >([]);
    const [openModal, setOpenModal] = useState(false);
    const [comparisonResults, setComparisonResults] = useState<any[]>([]);
    const [isComparing, setIsComparing] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [localSelections, setLocalSelections] = useState<
        Record<string, string>
    >({});

    useEffect(() => {
        const uniqueSuppliers = Array.from(
            new Set(Object.values(itemSupplierSelections)),
        );
        if (!hasInitializedSuppliers && uniqueSuppliers.length > 0) {
            setSelectedSupplierNames(uniqueSuppliers);
            setHasInitializedSuppliers(true);
        }
    }, [itemSupplierSelections, hasInitializedSuppliers]);

    useEffect(() => {
        if (openModal) {
            setLocalSelections(itemSupplierSelections);
        }
    }, [openModal, itemSupplierSelections]);

    async function handleCompare() {
        if (!projectId || selectedSupplierNames.length === 0) return;

        setOpenModal(true);
        setIsComparing(true);

        try {
            const results = await comparisonSvc.mutateAsync({
                SupplierNames: selectedSupplierNames,
                ProjectId: projectId,
                Category: "Materials",
            });
            setComparisonResults(Array.isArray(results) ? results : []);
        } catch (error) {
            console.error("[EstimateContract] supplier comparison failed", error);
            showError("Failed to compare suppliers");
            setOpenModal(false);
        } finally {
            setIsComparing(false);
        }
    }

    function onItemSupplierSelect(itemKey: string, supplierName: string) {
        setLocalSelections((prev) => ({ ...prev, [itemKey]: supplierName }));
    }

    async function handleConfirm() {
        setIsConfirming(true);

        try {
            const materialPatches: Record<string, Partial<LineEdit>> = {};

            await Promise.all(
                Object.entries(selectedValues).map(async ([editKey, item]) => {
                    const itemKey = item.manufacturerProductId || item.value;
                    const chosenSupplierName = localSelections[itemKey];
                    if (!chosenSupplierName) return;

                    const supplier = suppliers.find(
                        (entry: any) => entry.Name === chosenSupplierName,
                    );
                    if (!supplier) return;

                    const unitPrice = resolveComparisonUnitPrice(
                        comparisonResults,
                        chosenSupplierName,
                        item,
                    );

                    const payload: Record<string, unknown> = {
                        Id: item.id,
                        SupplierId: supplier.Id,
                    };
                    if (unitPrice !== null) {
                        payload.UnitPrice = unitPrice;
                    }

                    await updateCartItem(payload);

                    materialPatches[editKey] = {
                        supplierId: supplier.Id,
                        supplierName: supplier.Name,
                        supplierUnitPrice: normalizeUnitPrice(unitPrice),
                    };
                }),
            );

            if (Object.keys(materialPatches).length === 0) {
                showError("Select a supplier for at least one item");
                return;
            }

            patchMaterialEdits(materialPatches);
            setReferenceValue((prev: any) => {
                const materials = { ...prev.materials };
                for (const [editKey, patch] of Object.entries(materialPatches)) {
                    materials[editKey] = cloneMaterialEdit({
                        ...materials[editKey],
                        ...patch,
                    } as LineEdit);
                }
                return { ...prev, materials };
            });

            setOpenModal(false);
            showSuccess("Supplier selections saved");
        } catch (error) {
            console.error(
                "[EstimateContract] failed to save supplier selections",
                error,
            );
            showError("Failed to save supplier selections");
        } finally {
            setIsConfirming(false);
        }
    }

    return {
        suppliers,
        selectedValues,
        comparableItemCount,
        canCompare,
        hasUnsavedMaterialChanges,
        selectedSupplierNames,
        setSelectedSupplierNames,
        openModal,
        setOpenModal,
        comparisonResults,
        isComparing,
        isConfirming,
        isLoadingSuppliers,
        localSelections,
        itemSupplierSelections,
        handleCompare,
        onItemSupplierSelect,
        handleConfirm,
    };
}
